import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimActKc2VersionPriceRowData, BimActKc2VersionTotalItemData, BimPriceLevelEnum } from 'gql/graphql';
import { usePreviousValue } from 'hooks/usePrevious';
import type { BimActKc2DataExtended } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderActsKc2/types';
import { ActTotalDetailsType } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderActsKc2/types';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { actKc2TotalVersionDetailsGql, bimActKC2PriceVersionRowsGql } from '../bimWorkOrderActsKc2VersionQueries';

const totalsRequestId = 'bimActsKc2TotalVersionDetails';
const pricesRequestId = 'bimActKC2PriceVersionRows';

export const useBimActsKc2DetailsVersionData = (priceLevel: BimPriceLevelEnum | undefined) => {
    const dispatch = useDispatch();

    const [checkedActForDetails, setCheckedActForDetails] = useState<BimActKc2DataExtended | null>(null);
    const [actDetailsType, setActDetailsType] = useState<ActTotalDetailsType>(ActTotalDetailsType.Totals);
    const [localPriceLevel, setLocalPriceLevel] = useState<BimPriceLevelEnum | undefined>(priceLevel);
    const [totalsData, setTotalsData] = useState<BimActKc2VersionTotalItemData[] | null>();
    const [pricesData, setPricesData] = useState<BimActKc2VersionPriceRowData[] | null>();

    const prevPriceLevel = usePreviousValue(localPriceLevel);

    const [actKc2TotalDetailsData, actKc2TotalDetailsStatus, , actKc2TotalDetailsMakeRequest] = useRequest({
        requestId: totalsRequestId,
        query: actKc2TotalVersionDetailsGql,
        withEffect: false,
    });

    const [bimActKC2PriceRowsData, bimActKC2PriceRowsStatus, , bimActKC2PriceRowsMakeRequest] = useRequest({
        requestId: pricesRequestId,
        query: bimActKC2PriceVersionRowsGql,
        withEffect: false,
    });

    const [actKc2TotalDetailsSetData] = useResetStoreData({
        requestId: totalsRequestId,
    });

    const [bimActKC2PriceRowsSetData] = useResetStoreData({
        requestId: pricesRequestId,
    });

    const detailsTitle = useMemo(
        () => (checkedActForDetails ? `Акт КС-2 №${checkedActForDetails?.number}` : null),
        [checkedActForDetails],
    );

    const isDetailsOpened = !!checkedActForDetails;
    const isTotalsLoading = actKc2TotalDetailsStatus === RequestStatus.FETCHING;
    const isPricesLoading = bimActKC2PriceRowsStatus === RequestStatus.FETCHING;

    const closeDetails = useCallback(() => {
        setCheckedActForDetails(null);
        setTotalsData(undefined);
        setPricesData(undefined);
        setActDetailsType(ActTotalDetailsType.Totals);
        actKc2TotalDetailsSetData(undefined);
        bimActKC2PriceRowsSetData(undefined);
    }, [actKc2TotalDetailsSetData, bimActKC2PriceRowsSetData]);

    const refreshActsKc2TotalsTable = useCallback(() => {
        dispatch(actKc2TotalDetailsMakeRequest({ actKC2VersionId: checkedActForDetails?.id }));
    }, [dispatch, actKc2TotalDetailsMakeRequest, checkedActForDetails?.id]);

    const refreshActsKc2PricesTable = useCallback(() => {
        if (localPriceLevel) {
            dispatch(
                bimActKC2PriceRowsMakeRequest({
                    actKC2VersionId: checkedActForDetails?.id,
                    priceLevel: localPriceLevel,
                }),
            );
        }
    }, [bimActKC2PriceRowsMakeRequest, checkedActForDetails?.id, dispatch, localPriceLevel]);

    useEffect(() => {
        if (isDetailsOpened && actDetailsType === ActTotalDetailsType.Totals && !totalsData) {
            refreshActsKc2TotalsTable();
        }
    }, [actDetailsType, isDetailsOpened, refreshActsKc2TotalsTable, totalsData]);

    useEffect(() => {
        if (
            isDetailsOpened &&
            actDetailsType === ActTotalDetailsType.Prices &&
            (!pricesData || prevPriceLevel !== localPriceLevel)
        ) {
            refreshActsKc2PricesTable();
        }
    }, [
        actDetailsType,
        isDetailsOpened,
        pricesData,
        refreshActsKc2PricesTable,
        refreshActsKc2TotalsTable,
        totalsData,
        priceLevel,
        prevPriceLevel,
        localPriceLevel,
    ]);

    useEffect(() => {
        if (actKc2TotalDetailsStatus === RequestStatus.SUCCESS) {
            setTotalsData(actKc2TotalDetailsData?.bimActKC2VersionTotals?.items);
        }
    }, [actKc2TotalDetailsData?.bimActKC2VersionTotals?.items, actKc2TotalDetailsStatus]);

    useEffect(() => {
        if (bimActKC2PriceRowsStatus === RequestStatus.SUCCESS) {
            setPricesData(bimActKC2PriceRowsData?.bimActKC2VersionPriceRows?.items);
        }
    }, [bimActKC2PriceRowsData?.bimActKC2VersionPriceRows?.items, bimActKC2PriceRowsStatus]);

    return {
        isDetailsOpened,
        detailsTitle,
        checkedActId: checkedActForDetails?.id,
        isTotalsLoading,
        isPricesLoading,
        setCheckedActForDetails,
        closeDetails,
        refreshActsKc2TotalsTable,
        refreshActsKc2PricesTable,
        actDetailsType,
        setActDetailsType,
        totalsData,
        pricesData,
        setLocalPriceLevel,
        localPriceLevel,
    };
};
